import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { functions, db } from '../../lib/firebase';
import { CollectionPaths } from 'shared_schemas';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    mode?: 'import' | 'link';
    existingEventId?: string;
}

interface StartggEvent {
    id: number;
    name: string;
    slug: string;
    startAt: number;
    videogame: {
        id: number;
        name: string;
        slug: string;
    };
}

export function StartggImportModal({ open, onClose, onSuccess, mode = 'import', existingEventId }: Props) {
    const [step, setStep] = useState<'input' | 'select'>('input');
    const [urlOrSlug, setUrlOrSlug] = useState('');
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState<StartggEvent[]>([]);
    const [tournamentName, setTournamentName] = useState('');
    const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);
    const [errorMsg, setErrorMsg] = useState('');

    const parseUrl = httpsCallable(functions, 'parseTournamentUrl');
    const getEvents = httpsCallable(functions, 'getTournamentEvents');

    const handleFetchEvents = async () => {
        if (!urlOrSlug) return;
        setLoading(true);
        setErrorMsg('');

        try {
            let slug = urlOrSlug;
            // If it looks like a URL, try to parse it
            if (urlOrSlug.includes('start.gg/')) {
                const result = await parseUrl({ url: urlOrSlug });
                slug = (result.data as any).tournamentSlug;
            }

            const result = await getEvents({ slug });
            const data = result.data as any;
            setEvents(data.events || []);
            setTournamentName(data.tournamentName || slug);
            setStep('select');
        } catch (error: any) {
            console.error('Error fetching tournament:', error);
            setErrorMsg(error.message || 'Failed to fetch tournament details.');
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        if (selectedEventIds.length === 0) return;
        setLoading(true);
        setErrorMsg('');

        try {
            const selectedEvents = events.filter(e => selectedEventIds.includes(e.id));

            if (mode === 'link') {
                if (selectedEvents.length > 1) {
                    setErrorMsg("You can only link one Start.gg event to this existing event.");
                    setLoading(false);
                    return;
                }
                if (!existingEventId) {
                    setErrorMsg("Missing existing event ID.");
                    setLoading(false);
                    return;
                }

                const event = selectedEvents[0];
                const eventRef = doc(db, CollectionPaths.events, existingEventId);

                // Update existing event with Start.gg data
                await updateDoc(eventRef, {
                    startggId: event.id.toString(),
                    startggSlug: event.slug,
                    startggUrl: `https://start.gg/${event.slug}`,
                    games: [event.videogame.slug], // Optional: overwrite or append? Overwrite safest for sync.
                    updatedAt: serverTimestamp(),
                });

            } else {
                // IMPORT MODE
                const batch = [];
                for (const event of selectedEvents) {
                    // Create EventDocument
                    const eventRef = doc(collection(db, CollectionPaths.events));
                    const eventDate = new Date(event.startAt * 1000);

                    batch.push(setDoc(eventRef, {
                        id: eventRef.id,
                        title: `${tournamentName} - ${event.name}`,
                        date: eventDate.toISOString().split('T')[0], // YYYY-MM-DD
                        type: 'competition',
                        games: [event.videogame.slug],
                        location: 'See Start.gg', // Placeholder
                        startTime: eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
                        endTime: '', // Unknown
                        requiresPermission: false, // Default
                        inviteOnly: false,
                        status: 'scheduled',
                        startggId: event.id.toString(),
                        startggSlug: event.slug,
                        startggUrl: `https://start.gg/${event.slug}`,
                        attendanceCount: 0,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                    }));
                }
                await Promise.all(batch);
            }

            onSuccess();
            onClose();
            // Reset
            setStep('input');
            setUrlOrSlug('');
            setEvents([]);
            setSelectedEventIds([]);

        } catch (error: any) {
            console.error('Operation failed:', error);
            setErrorMsg('Failed to process event(s).');
        } finally {
            setLoading(false);
        }
    };


    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-secondary-800 rounded-xl border border-secondary-700 shadow-2xl w-full max-w-lg">
                {/* Header */}
                <div className="sticky top-0 bg-secondary-800 border-b border-secondary-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
                    <h2 className="text-xl font-display font-bold text-white">
                        {mode === 'link' ? 'Link with Start.gg' : 'Import from Start.gg'}
                    </h2>
                    <button onClick={onClose} className="text-secondary-400 hover:text-white text-xl">✕</button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {errorMsg && (
                        <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-sm border border-red-500/50">
                            {errorMsg}
                        </div>
                    )}

                    {step === 'input' && (
                        <div className="space-y-4">
                            <p className="text-secondary-300">
                                Enter the tournament URL or slug to find events.
                            </p>
                            <div>
                                <label className="label">Tournament URL or Slug</label>
                                <input
                                    type="text"
                                    placeholder="https://www.start.gg/tournament/..."
                                    value={urlOrSlug}
                                    onChange={(e) => setUrlOrSlug(e.target.value)}
                                    className="input"
                                />
                            </div>
                        </div>
                    )}

                    {step === 'select' && (
                        <div className="space-y-4">
                            <h3 className="font-medium text-sm text-secondary-400">
                                {mode === 'link'
                                    ? `Select the ONE event to link from ${tournamentName}`
                                    : `Select Events from ${tournamentName}`
                                }
                            </h3>
                            <div className="max-h-[300px] overflow-y-auto space-y-2 border border-secondary-700 rounded-md p-2 bg-secondary-900/50">
                                {events.map((event) => (
                                    <label key={event.id} className="flex items-center space-x-3 p-2 hover:bg-secondary-800 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedEventIds.includes(event.id)}
                                            onChange={(e) => {
                                                if (mode === 'link') {
                                                    // Single select for linking
                                                    setSelectedEventIds([event.id]);
                                                } else {
                                                    if (e.target.checked) {
                                                        setSelectedEventIds([...selectedEventIds, event.id]);
                                                    } else {
                                                        setSelectedEventIds(selectedEventIds.filter(id => id !== event.id));
                                                    }
                                                }
                                            }}
                                            className="form-checkbox h-5 w-5 text-primary-500 rounded border-secondary-500 bg-secondary-700 focus:ring-offset-secondary-900"
                                        />
                                        <div className="text-sm font-medium text-white">
                                            {event.name}
                                            <span className="ml-2 text-xs text-secondary-400">({event.videogame.name})</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-secondary-800 border-t border-secondary-700 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
                    {step === 'input' ? (
                        <>
                            <button onClick={onClose} className="btn-secondary">Cancel</button>
                            <button
                                onClick={handleFetchEvents}
                                disabled={!urlOrSlug || loading}
                                className="btn-primary"
                            >
                                {loading ? 'Searching...' : 'Find Events'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setStep('input')} className="btn-secondary">Back</button>
                            <button
                                onClick={handleImport}
                                disabled={selectedEventIds.length === 0 || loading}
                                className="btn-primary"
                            >
                                {loading ? (mode === 'link' ? 'Linking...' : 'Importing...') : (mode === 'link' ? 'Link Event' : 'Import Selected')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
